import { Icon } from "common/Icon";
import { EXPORT_TROUTE_MODAL } from "common/modals/ExportTRouteModal";
import { QUICK_EXPORT_MODAL } from "common/modals/QuickExportModal";
import { CircleSpinner } from "common/spinners";
import { CTA } from "common/styleguide/CTA";
import { PopoverLinkMenu, PopoverLinkMenuHeading, PopoverLinkMenuItem } from "common/styleguide/PopoverLinkMenu";
import { setPreference } from "lib/api/currentUser";
import {
  formatsForObjectType,
  SMART_EXPORT_DEFAULT,
  SMART_EXPORT_FILE_DOWNLOAD,
  SMART_EXPORT_PHONE_PUSH,
} from "lib/exportFormats";
import fetchJSON from "lib/fetchJSON";
import { featureNames, Msg } from "lib/localization";
import { dismissModal, showModal } from "lib/modalUtils";
import { userIsAdmin } from "lib/utils";
import p from "prop-types";
import React, { Fragment } from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { connect } from "react-redux";
import styles from "./SmartExport.scss";

const messages = defineMessages({
  moreOptions: {
    id: "SmartExport.moreOptions",
    defaultMessage: "More Export Options",
  },
  resend: {
    id: "SmartExport.resend",
    defaultMessage: "Click to re-send",
  },
  sendToDifferent: {
    id: "SmartExport.sendToDifferent",
    defaultMessage: "Send to a different device",
  },
  sendToPhone: {
    id: "SmartExport.sendToPhone",
    defaultMessage: "Send to phone",
  },
});

export class _SmartExport extends React.PureComponent {
  static propTypes = {
    currentUser: p.object.isRequired,
    route: p.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      pushPending: false,
      pushSent: false,
      pushError: false,
    };
  }

  toggleDropdown = () => {
    this.setState((state) => ({ expanded: !state.expanded }));
  };

  getExportState = () => {
    const { currentUser } = this.props;
    return (currentUser && currentUser.preferences.get("smart_export_state")) || SMART_EXPORT_DEFAULT;
  };

  getStickyFileFormat = () => {
    const { currentUser } = this.props;
    return (currentUser && currentUser.preferences.get("smart_export_file_format")) || "fit";
  };

  buttonClicked = () => {
    const { sendToDevice } = this.props;
    const { pushPending } = this.state;
    const exportState = this.getExportState();

    if (pushPending) {
      return;
    }

    if (exportState === SMART_EXPORT_DEFAULT) {
      sendToDevice();
      this.setState({ pushPending: false, pushSent: false, pushError: false });
    }

    if (exportState === SMART_EXPORT_FILE_DOWNLOAD) {
      this.showQuickExport();
    }

    if (exportState === SMART_EXPORT_PHONE_PUSH) {
      this.sendPhonePush();
    }
  };

  sendPhonePush = () => {
    const { route, setPreference } = this.props;

    this.setState({
      expanded: false,
      pushPending: true,
      pushSent: false,
      pushError: false,
    });
    fetchJSON(`/routes/${route.id}/send_push_notification`, {}, { method: "POST" })
      .then(() => {
        this.setState({ pushPending: false, pushSent: true, pushError: false });
        setPreference("smart_export_state", SMART_EXPORT_PHONE_PUSH);
      })
      .catch(() => {
        this.setState({ pushPending: false, pushSent: false, pushError: true });
      });
  };

  hideQuickExport = () => {
    this.setState(
      {
        fileFormatOverride: null,
      },
      () => dismissModal(QUICK_EXPORT_MODAL)
    );
  };

  showFileExportModal = () => {
    showModal(EXPORT_TROUTE_MODAL, {
      troute: this.props.route,
      objectType: "route",
    });

    this.setState({
      expanded: false,
      pushPending: false,
      pushError: false,
      pushSent: false,
    });
  };

  downloadFitClicked = () => {
    this.setState(
      {
        fileFormatOverride: "fit",
        expanded: false,
        pushPending: false,
        pushError: false,
        pushSent: false,
      },
      () => this.showQuickExport()
    );
  };

  resetPreferences = () => {
    setPreference("smart_export_state", null);
    setPreference("smart_export_show_phone_push_option", null);
    this.setState({ expanded: false });
    window.location.reload();
  };

  showQuickExport = () => {
    const { route } = this.props;
    const format = this.state.fileFormatOverride || this.getStickyFileFormat();
    showModal(QUICK_EXPORT_MODAL, { route, format });
  };

  render() {
    const { expanded, pushSent, pushPending, pushError } = this.state;
    const { sendToDevice, currentUser, intl } = this.props;
    const stickyFileFormat = this.getStickyFileFormat();
    const originalExportState = this.getExportState();

    // If a push is or was in progress, show we're in 'phone_push', otherwise proceed as normal.
    // this is here so that sending a push from the dropdown will still show the right in-progress messages
    // Clicking any other button or item from the a dropdown should clear all push statuses
    const exportState = pushPending || pushError || pushSent ? SMART_EXPORT_PHONE_PUSH : originalExportState;

    // being super defensive here becuase pref map key change caused a bunch of problems
    const formats = formatsForObjectType("route");
    const f = formats.get(stickyFileFormat) || formats.get("fit");

    const showPhonePushOption = currentUser.preferences.get("smart_export_show_phone_push_option");

    return (
      <div className={styles.SmartExport}>
        <CTA
          margins={false}
          className={styles.primary}
          onClick={this.buttonClicked}
          data-tip={pushError}
          title={pushError ? intl.formatMessage(messages.resend) : ""}
        >
          {exportState === SMART_EXPORT_DEFAULT && intl.formatMessage(featureNames.sendToDevice)}
          {exportState === SMART_EXPORT_FILE_DOWNLOAD && (
            <T id="SmartExport.exportFile" defaultMessage="Export {type} File" values={{ type: f.ext.toUpperCase() }} />
          )}
          {exportState === SMART_EXPORT_PHONE_PUSH && (
            <Fragment>
              {pushPending && (
                <Fragment>
                  <CircleSpinner size={15} className={styles.spinner} />{" "}
                  <T id="SmartExport.sendingRoute" defaultMessage="Sending route" />
                </Fragment>
              )}
              {pushSent && (
                <Fragment>
                  <Icon i="md-checkmark" size={15} />{" "}
                  <T id="SmartExport.notificationSent" defaultMessage="Notification Sent" />
                </Fragment>
              )}
              {pushError && <T id="SmartExport.pushError" defaultMessage="Phone Push Error" />}
              {!pushSent && !pushPending && !pushError && <Msg m={messages.sendToPhone} />}
            </Fragment>
          )}
        </CTA>
        <CTA
          ref={(r) => {
            this._menuAnchor = r;
          }}
          onClick={this.toggleDropdown}
          margins={false}
          className={styles.more}
        >
          <Icon i="ios-more-rotated" />
        </CTA>
        {expanded && (
          <PopoverLinkMenu
            intl={intl}
            target={this._menuAnchor}
            placement="bottom"
            onRequestClose={this.toggleDropdown}
          >
            {exportState === SMART_EXPORT_DEFAULT && (
              <Fragment>
                {showPhonePushOption && (
                  <PopoverLinkMenuItem onClick={this.sendPhonePush}>
                    <Msg m={messages.sendToPhone} />
                  </PopoverLinkMenuItem>
                )}
                <PopoverLinkMenuItem onClick={this.downloadFitClicked}>
                  <T id="SmartExport.downloadFIT" defaultMessage="Download .FIT file" />
                </PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={this.showFileExportModal}>
                  <Msg m={messages.moreOptions} />
                </PopoverLinkMenuItem>
              </Fragment>
            )}
            {exportState === SMART_EXPORT_FILE_DOWNLOAD && (
              <Fragment>
                {showPhonePushOption && (
                  <PopoverLinkMenuItem onClick={this.sendPhonePush}>
                    <Msg m={messages.sendToPhone} />
                  </PopoverLinkMenuItem>
                )}
                <PopoverLinkMenuItem onClick={() => sendToDevice()}>
                  <Msg m={messages.sendToDifferent} />
                </PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={this.showFileExportModal}>
                  <Msg m={messages.moreOptions} />
                </PopoverLinkMenuItem>
              </Fragment>
            )}
            {exportState === SMART_EXPORT_PHONE_PUSH && (
              <Fragment>
                <PopoverLinkMenuItem onClick={() => sendToDevice()}>
                  <Msg m={messages.sendToDifferent} />
                </PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={this.showFileExportModal}>
                  <Msg m={messages.moreOptions} />
                </PopoverLinkMenuItem>
              </Fragment>
            )}
            {userIsAdmin && (
              <Fragment>
                <PopoverLinkMenuHeading>RWGPS Admin</PopoverLinkMenuHeading>
                <PopoverLinkMenuItem onClick={this.resetPreferences}>Clear state</PopoverLinkMenuItem>
              </Fragment>
            )}
          </PopoverLinkMenu>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.currentUser,
});

const mapDispatchToProps = {
  setPreference: setPreference,
};

export const SmartExport = connect(mapStateToProps, mapDispatchToProps)(injectIntl(_SmartExport));
